<template>
  <div class="wl-shell">
    <!-- =========================================================
         Nagłówek: tytuł z zielonym akcentem (biała = zaufane)
         + zestaw akcji. W trybie listy delikatny podtytuł kontekstu.
         ========================================================= -->
    <header class="wl-header">
      <div class="wl-header__title-wrap">
        <span class="wl-header__accent" aria-hidden="true"></span>
        <div>
          <h1 class="wl-header__title">
            {{ pageTitle }}
            <span
              v-if="currentView === 'list' && vehicles.length > 0"
              class="wl-header__count"
              :aria-label="t('wanted-stats-plates')"
            >{{ vehicles.length }}</span>
          </h1>
          <p v-if="currentView === 'list'" class="wl-header__subtitle">
            {{ t('wanted-subtitle-white') }}
          </p>
        </div>
      </div>

      <div class="wl-header__actions">
        <template v-if="currentView === 'list'">
          <button
            type="button"
            class="button-save"
            :disabled="!hasContactGroups"
            :title="!hasContactGroups ? t('no-contact-groups') : ''"
            @click="openAdd"
          >
            <span class="wl-btn-icon" aria-hidden="true">+</span>
            {{ t('add') }}
          </button>
          <button
            type="button"
            class="button-add-user"
            :disabled="!hasContactGroups"
            @click="openCsv"
          >
            {{ t('import-csv') }}
          </button>
        </template>
        <template v-else>
          <button type="button" class="button-add-user" @click="backToList">
            ← {{ t('back-to-list') }}
          </button>
        </template>
      </div>
    </header>

    <!-- =========================================================
         Pasek statystyk — tylko na widoku listy.
         ========================================================= -->
    <section
      v-if="currentView === 'list' && vehicles.length > 0"
      class="wl-stats"
      aria-label="statystyki"
    >
      <div class="wl-stat">
        <span class="wl-stat__num">{{ vehicles.length }}</span>
        <span class="wl-stat__label">{{ t('wanted-stats-plates') }}</span>
      </div>
      <div class="wl-stat wl-stat--hits">
        <span class="wl-stat__num">{{ totalHits }}</span>
        <span class="wl-stat__label">{{ t('wanted-stats-hits') }}</span>
      </div>
      <div class="wl-stat wl-stat--groups">
        <span class="wl-stat__num">{{ activeGroupsCount }}</span>
        <span class="wl-stat__label">{{ t('wanted-stats-groups') }}</span>
      </div>
    </section>

    <p v-if="!hasContactGroups && currentView === 'list'" class="wl-alert wl-alert--warning" role="note">
      <span class="wl-alert__icon" aria-hidden="true">!</span>
      {{ t('no-contact-groups') }}
    </p>

    <!-- =========================================================
         Pasek filtra — szybkie zawężenie listy bez round-tripa do API.
         ========================================================= -->
    <div
      v-if="currentView === 'list' && vehicles.length > 5"
      class="wl-toolbar"
    >
      <label class="wl-search">
        <svg class="wl-search__icon" viewBox="0 0 20 20" aria-hidden="true">
          <circle cx="9" cy="9" r="6.5" fill="none" stroke="currentColor" stroke-width="1.7" />
          <line x1="14" y1="14" x2="18" y2="18" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
        </svg>
        <input
          v-model="filterQuery"
          type="search"
          class="wl-search__input"
          :placeholder="t('wanted-filter-placeholder')"
          :aria-label="t('search')"
          autocomplete="off"
        />
        <button
          v-if="filterQuery"
          type="button"
          class="wl-search__clear"
          :aria-label="t('cancel')"
          @click="filterQuery = ''"
        >×</button>
      </label>
      <span class="wl-toolbar__count" aria-live="polite">
        {{ filteredVehicles.length }} / {{ vehicles.length }}
      </span>
    </div>

    <transition name="wanted-fade" mode="out-in">
      <!-- ========================== LISTA ========================== -->
      <div v-if="currentView === 'list'" key="list">
        <div v-if="vehicles.length > 0" class="wl-table-card">
          <table class="wl-table">
            <thead>
              <tr>
                <th class="wl-col-num">{{ t('ordinal-number') }}</th>
                <th class="wl-col-plate">{{ t('short-plate-no') }}</th>
                <th class="wl-col-hits">{{ t('hits-no') }}</th>
                <th class="wl-col-since">{{ t('monitored-since') }}</th>
                <th class="wl-col-desc">{{ t('vehicle-description') }}</th>
                <th class="wl-col-notifies">{{ t('wanted-notifies-column') }}</th>
                <th class="wl-col-action">{{ t('action') }}</th>
              </tr>
            </thead>
            <transition-group tag="tbody" name="wl-row">
              <tr
                v-for="(vehicle, index) in filteredVehicles"
                :key="vehicle.id"
                :class="{ 'wl-row--pending': pendingDeleteId === vehicle.id }"
              >
                <td class="wl-col-num">{{ index + 1 }}</td>
                <td class="wl-col-plate">
                  <span class="wl-plate">{{ vehicle.licensePlate }}</span>
                </td>
                <td class="wl-col-hits">
                  <span
                    class="wl-hits"
                    :class="{ 'wl-hits--zero': vehicle.hits === 0, 'wl-hits--many': vehicle.hits >= 10 }"
                  >{{ vehicle.hits }}</span>
                </td>
                <td class="wl-col-since">{{ formatDateTime(vehicle.monitoredSince) }}</td>
                <td class="wl-col-desc">
                  <span v-if="vehicle.description">{{ vehicle.description }}</span>
                  <span v-else class="wl-readonly">—</span>
                </td>
                <td class="wl-col-notifies">
                  <div v-if="vehicle.contactGroups.length > 0" class="wl-chips">
                    <ContactGroupChip
                      v-for="g in vehicle.contactGroups"
                      :key="g.id"
                      :group="g"
                      :clickable="false"
                    />
                  </div>
                  <span v-else class="wl-readonly">—</span>
                </td>
                <td class="wl-col-action">
                  <template v-if="canEditPlate(vehicle)">
                    <!-- Dwustopniowe usuwanie: szybciej dla power-userów,
                         bezpieczniej dla pomyłek („potwierdź" zamiast modala). -->
                    <div v-if="pendingDeleteId === vehicle.id" class="wl-confirm">
                      <button
                        type="button"
                        class="button-delete button-compact"
                        @click="confirmDelete(vehicle.id)"
                      >{{ t('wanted-confirm-delete') }}</button>
                      <button
                        type="button"
                        class="button-add-user button-compact"
                        @click="cancelDelete"
                      >{{ t('cancel') }}</button>
                    </div>
                    <button
                      v-else
                      type="button"
                      class="button-delete button-compact"
                      @click="requestDelete(vehicle.id)"
                    >{{ t('delete') }}</button>
                  </template>
                  <span v-else class="wl-readonly">—</span>
                </td>
              </tr>
            </transition-group>
          </table>

          <!-- Brak wyników po filtrze — odróżniony od pustej listy. -->
          <div v-if="filteredVehicles.length === 0" class="wl-empty-filter">
            <p>{{ t('wanted-filter-no-results') }}</p>
            <button
              type="button"
              class="button-add-user button-compact"
              @click="filterQuery = ''"
            >{{ t('cancel') }}</button>
          </div>
        </div>

        <!-- Empty state — wyraziste, ale spokojne. -->
        <div v-else class="wl-empty">
          <svg class="wl-empty__icon" viewBox="0 0 64 64" aria-hidden="true">
            <rect x="8" y="20" width="48" height="24" rx="3" fill="#fff" stroke="#cfcfca" stroke-width="2" />
            <rect x="14" y="26" width="36" height="12" rx="2" fill="#f8f8f5" stroke="#e3e3de" stroke-width="1.5" />
            <path d="M22 30h20M22 34h12" stroke="#b5b5b1" stroke-width="1.6" stroke-linecap="round" />
            <circle cx="48" cy="48" r="9" fill="#fff" stroke="#1f8f2f" stroke-width="2" />
            <path d="M44 48l3 3 5-6" fill="none" stroke="#1f8f2f" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <p class="wl-empty__title">{{ t('no-vehicles-on-white-list') }}</p>
        </div>
      </div>

      <!-- ====================== INNE WIDOKI ======================= -->
      <WantedPlateForm
        v-else-if="currentView === 'add'"
        key="add"
        list-type="white"
        :validity-period-enabled="false"
        :submitting="actionSubmitting"
        @submit="onAddSubmit"
        @cancel="backToList"
      />

      <WantedCsvImport
        v-else
        key="csv"
        list-type="white"
        :available-groups="wantedStore.contactGroups"
        :submitting="actionSubmitting"
        @submit="onCsvSubmit"
        @cancel="backToList"
      />
    </transition>

    <transition name="wl-toast">
      <div
        v-if="statusMessage && !statusDismissed"
        class="wl-alert wl-alert--dismissible"
        :class="statusType === 'success' ? 'wl-alert--success' : 'wl-alert--danger'"
        role="alert"
      >
        <span class="wl-alert__icon" aria-hidden="true">
          {{ statusType === 'success' ? '✓' : '!' }}
        </span>
        <span class="wl-alert__msg">{{ statusMessage }}</span>
        <button
          type="button"
          class="btn-close wl-alert__close"
          aria-label="Close"
          @click="dismissStatus"
        >×</button>
      </div>
    </transition>
  </div>
</template>

<script>
import WhiteListsService from '@/services/WhiteListsService';
import ContactGroupChip from '@/components/wanted/ContactGroupChip.vue';
import WantedPlateForm from '@/components/wanted/WantedPlateForm.vue';
import WantedCsvImport from '@/components/wanted/WantedCsvImport.vue';
import { useUserStore } from '@/stores/user.store';
import { useWantedStore } from '@/stores/wanted.store';

export default {
  name: 'WhiteList',
  components: {
    ContactGroupChip,
    WantedPlateForm,
    WantedCsvImport,
  },
  data() {
    return {
      vehicles: [],
      currentView: 'list', // 'list' | 'add' | 'csv'
      actionSubmitting: false,
      statusMessage: '',
      statusType: 'error',
      statusDismissed: false,
      // Szybki filtr w-locie + dwustopniowe usuwanie.
      filterQuery: '',
      pendingDeleteId: null,
    };
  },
  computed: {
    wantedStore() { return useWantedStore(); },
    hasContactGroups() { return this.wantedStore.contactGroups.length > 0; },
    pageTitle() {
      if (this.currentView === 'add') return this.t('add-to-white-list');
      if (this.currentView === 'csv') return `${this.t('import-csv')} → ${this.t('white-list')}`;
      return this.t('white-list').toUpperCase();
    },
    filteredVehicles() {
      const q = this.filterQuery.trim().toLowerCase();
      if (!q) return this.vehicles;
      return this.vehicles.filter(v => {
        const plate = (v.licensePlate || '').toLowerCase();
        const desc  = (v.description  || '').toLowerCase();
        return plate.includes(q) || desc.includes(q);
      });
    },
    totalHits() {
      return this.vehicles.reduce((sum, v) => sum + (Number(v.hits) || 0), 0);
    },
    activeGroupsCount() {
      const ids = new Set();
      this.vehicles.forEach(v => v.contactGroupIds.forEach(id => ids.add(id)));
      return ids.size;
    },
  },
  methods: {
    mapVehicleFromApi(vehicle) {
      const brief = Array.isArray(vehicle.contactGroupsBrief) ? vehicle.contactGroupsBrief : [];
      return {
        id: vehicle.id,
        licensePlate: vehicle.plateLpr,
        description: vehicle.description ?? '',
        hits: vehicle.hitCounter ?? 0,
        monitoredSince: vehicle.createdAt || new Date().toISOString(),
        contactGroupIds: Array.isArray(vehicle.contactGroupIds) ? vehicle.contactGroupIds : brief.map(g => g.id),
        contactGroups: brief.map(g => ({
          id: g.id,
          name: g.name,
          contactsSummary: g.contactsSummary ?? { count: 0, emails: 0, sms: 0, mms: 0 },
        })),
      };
    },
    canEditPlate(vehicle) {
      const userStore = useUserStore();
      return userStore.canEditWantedPlate({ contactGroupIds: vehicle.contactGroupIds });
    },
    async fetchVehicles() {
      try {
        const response = await WhiteListsService.getAll();
        this.vehicles = (Array.isArray(response) ? response : []).map(this.mapVehicleFromApi);
      } catch (error) {
        if (error === 404 || error?.status === 404) {
          this.vehicles = [];
          return;
        }
        console.error('Error fetching vehicles:', error);
        this.vehicles = [];
      }
    },
    requestDelete(id) {
      this.pendingDeleteId = id;
    },
    cancelDelete() {
      this.pendingDeleteId = null;
    },
    async confirmDelete(id) {
      this.pendingDeleteId = null;
      try {
        await WhiteListsService.delete(id);
        this.vehicles = this.vehicles.filter(v => v.id !== id);
        this.showStatus(this.t('wanted-plate-deleted-white'), 'success');
      } catch (error) {
        console.error('Error deleting vehicle:', error);
        this.showStatus(this.t('wanted-plate-delete-error'), 'error');
      }
    },
    formatDateTime(dateString) {
      const date = new Date(dateString);
      const pad = n => String(n).padStart(2, '0');
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    },
    openAdd() {
      if (!this.hasContactGroups) {
        this.showStatus(this.t('no-contact-groups'), 'error');
        return;
      }
      this.currentView = 'add';
    },
    openCsv() {
      if (!this.hasContactGroups) {
        this.showStatus(this.t('no-contact-groups'), 'error');
        return;
      }
      this.currentView = 'csv';
    },
    backToList() {
      this.currentView = 'list';
      this.pendingDeleteId = null;
    },
    async onAddSubmit(payload) {
      this.actionSubmitting = true;
      try {
        const response = await WhiteListsService.postNewPlate(
          payload.plateLpr,
          payload.description,
          payload.contactGroupIds
        );
        this.vehicles.unshift(this.mapVehicleFromApi(response));
        this.showStatus(this.t('wanted-plate-added-white'), 'success');
        this.currentView = 'list';
      } catch (error) {
        console.error('Error adding vehicle:', error);
        this.showStatus(this.parseApiError(error, this.t('wanted-plate-add-error')), 'error');
      } finally {
        this.actionSubmitting = false;
      }
    },
    async onCsvSubmit({ rows, contactGroupIds }) {
      this.actionSubmitting = true;
      let success = 0;
      let failed = 0;
      for (const row of rows) {
        try {
          const response = await WhiteListsService.postNewPlate(
            row.licensePlate,
            row.description,
            contactGroupIds
          );
          this.vehicles.unshift(this.mapVehicleFromApi(response));
          success++;
        } catch (error) {
          console.error('Error adding vehicle from CSV:', error);
          failed++;
        }
      }
      this.actionSubmitting = false;
      this.currentView = 'list';
      this.showStatus(
        this.t('wanted-csv-import-result', { success, failed }),
        failed === 0 ? 'success' : 'error'
      );
    },
    parseApiError(error, fallback) {
      if (typeof error === 'string' && error.trim() !== '') return error;
      if (error?.message) return error.message;
      return fallback;
    },
    showStatus(message, type = 'error') {
      this.statusMessage = message;
      this.statusType = type;
      this.statusDismissed = false;
    },
    dismissStatus() { this.statusDismissed = true; },
  },
  async mounted() {
    await this.wantedStore.fetchContactGroups();
    await this.fetchVehicles();
  }
};
</script>

<style scoped>
/* =============================================================
   Lokalna paleta — zieleń jako tożsamość białej listy (zaufane),
   neutralne szarości jako tło i ramy. Trzymamy się tej samej
   strukury i miar co czarna lista — różni je tylko akcent.
   ============================================================= */
.wl-shell {
  --wl-green:        #1f8f2f;
  --wl-green-deep:   #156722;
  --wl-ink:          #1f2937;
  --wl-ink-soft:     #4b5563;
  --wl-muted:        #7a7a76;
  --wl-line:         #e3e3de;
  --wl-line-soft:    #ececea;
  --wl-bg:           #fafaf8;
  --wl-bg-hover:     #ededea;

  padding: 1.5rem 1.3rem 2.5rem;
  /* Stabilna szerokość: wypełniamy parent (.content-wrap) i nigdy go nie rozpychamy.
     Bez `min-width: 0` flex-child puchnie do natywnej szerokości tabeli i wpycha
     się w obszar lewego paska filtrów. */
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  margin: 0;
  color: var(--wl-ink);
}

/* =============================================================
   Header — "hero" sekcja: gradientowy akcent + tytuł + podtytuł.
   ============================================================= */
.wl-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--wl-line);
  position: relative;
}
.wl-header::after {
  /* Subtelny gradient u dołu nagłówka — domyka komozycję od dołu. */
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 1px;
  background: linear-gradient(90deg, var(--wl-green) 0%, transparent 30%);
  opacity: 0.4;
}
.wl-header__title-wrap {
  display: flex;
  align-items: stretch;
  gap: 0.9rem;
  flex: 1;
  min-width: 0;
}
.wl-header__accent {
  width: 5px;
  border-radius: 3px;
  background: linear-gradient(180deg, var(--wl-green) 0%, var(--wl-green-deep) 100%);
  box-shadow: 0 0 0 1px rgba(31, 143, 47, 0.15), 0 4px 10px rgba(21, 103, 34, 0.18);
  flex-shrink: 0;
}
.wl-header__title {
  margin: 0;
  font-size: 1.55rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 1.15;
  color: var(--wl-ink);
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
}
.wl-header__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 1.6rem;
  padding: 0 0.55rem;
  border-radius: 999px;
  background: linear-gradient(180deg, var(--wl-green) 0%, var(--wl-green-deep) 100%);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 1px 2px rgba(21, 103, 34, 0.35);
}
.wl-header__subtitle {
  margin: 0.35rem 0 0;
  font-size: 0.88rem;
  color: var(--wl-muted);
  letter-spacing: 0.01em;
}
.wl-header__actions {
  display: flex;
  gap: 0.55rem;
  flex-shrink: 0;
  align-self: center;
}
.wl-btn-icon {
  font-weight: 700;
  font-size: 1rem;
  line-height: 1;
  transform: translateY(-1px);
}

/* =============================================================
   Pasek statystyk — 3 tile'y; tabular-nums dla równego prawego kantu.
   ============================================================= */
.wl-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.85rem;
  margin-bottom: 1rem;
}
.wl-stat {
  position: relative;
  background-color: #fff;
  border: 1px solid var(--wl-line);
  border-radius: 12px;
  padding: 0.85rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.wl-stat:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.07);
}
.wl-stat::before {
  /* Kropkowane tło — bardzo subtelne, dla wartości artystycznej. */
  content: "";
  position: absolute;
  right: -10px;
  top: -10px;
  width: 80px;
  height: 80px;
  background: radial-gradient(circle, rgba(31, 143, 47, 0.06) 1px, transparent 1.5px);
  background-size: 9px 9px;
  pointer-events: none;
}
.wl-stat--hits::before    { background: radial-gradient(circle, rgba(1, 82, 161, 0.06) 1px, transparent 1.5px); background-size: 9px 9px; }
.wl-stat--groups::before  { background: radial-gradient(circle, rgba(182, 109, 11, 0.06) 1px, transparent 1.5px); background-size: 9px 9px; }
.wl-stat__num {
  font-size: 1.7rem;
  font-weight: 800;
  line-height: 1.1;
  color: var(--wl-ink);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}
.wl-stat__label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--wl-muted);
}

/* =============================================================
   Toolbar z filtrem — pojawia się przy > 5 wpisów.
   ============================================================= */
.wl-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.wl-search {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  background-color: #fff;
  border: 1px solid var(--wl-line);
  border-radius: 8px;
  padding: 0 0.6rem 0 2.25rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  height: 38px;
}
.wl-search:focus-within {
  border-color: var(--wl-green);
  box-shadow: 0 0 0 3px rgba(31, 143, 47, 0.12);
}
.wl-search__icon {
  position: absolute;
  left: 0.7rem;
  top: 50%;
  width: 16px;
  height: 16px;
  transform: translateY(-50%);
  color: var(--wl-muted);
  pointer-events: none;
}
.wl-search__input {
  flex: 1;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 0.9rem;
  color: var(--wl-ink);
  height: 100%;
  padding: 0;
}
.wl-search__input::placeholder { color: #b1b1ac; }
.wl-search__input::-webkit-search-decoration,
.wl-search__input::-webkit-search-cancel-button { display: none; }
.wl-search__clear {
  background: transparent;
  border: 0;
  font-size: 1.2rem;
  cursor: pointer;
  color: var(--wl-muted);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  min-width: 22px !important;
  min-height: 22px !important;
  line-height: 1;
}
.wl-search__clear:hover { color: var(--wl-green); background-color: #f0faf1 !important; }
.wl-toolbar__count {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--wl-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* =============================================================
   Karta z tabelą — soft shadow, sticky thead, polish na hover.
   ============================================================= */
.wl-table-card {
  background-color: #fff;
  border: 1px solid var(--wl-line);
  border-radius: 12px;
  /* Wewnętrzny scroll w poziomie — gdy okno za wąskie, tabela przewija się
     wewnątrz karty zamiast rozpychać cały widok. */
  overflow-x: auto;
  overflow-y: hidden;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 8px 24px -16px rgba(15, 23, 42, 0.12);
  margin-bottom: 1rem;
}
.wl-table {
  width: 100%;
  /* `fixed` — szerokości kolumn z CSS są respektowane, treść nie rozpycha tabeli. */
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
}
/* Długie opisy / nazwy grup łamią się zamiast wymuszać poziomy scroll. */
.wl-table td { word-break: break-word; overflow-wrap: anywhere; }
.wl-table th,
.wl-table td {
  padding: 0.75rem 0.95rem;
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid var(--wl-line-soft);
}
.wl-table th.wl-col-num,
.wl-table td.wl-col-num,
.wl-table th.wl-col-hits,
.wl-table td.wl-col-hits { text-align: right; }
.wl-table th.wl-col-action,
.wl-table td.wl-col-action { text-align: right; white-space: nowrap; }
.wl-table thead th {
  background-color: #f6f6f3;
  font-weight: 700;
  color: var(--wl-ink-soft);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 2px solid var(--wl-ink);
  position: sticky;
  top: 0;
  z-index: 1;
  white-space: nowrap;
}
.wl-table tbody tr {
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}
.wl-table tbody tr:nth-child(even) {
  background-color: var(--wl-bg);
}
.wl-table tbody tr:hover {
  background-color: var(--wl-bg-hover);
  box-shadow: inset 3px 0 0 var(--wl-green);
}
.wl-table tbody tr:last-child td { border-bottom: 0; }
.wl-table tbody tr.wl-row--pending {
  background-color: #fff1f1;
  box-shadow: inset 3px 0 0 #b91c1c;
}

/* Procentowe szerokości — tabela skaluje się razem z kontenerem. */
.wl-col-num      { width: 4%;  color: var(--wl-muted); font-variant-numeric: tabular-nums; }
.wl-col-plate    { width: 13%; }
.wl-col-hits     { width: 7%;  font-variant-numeric: tabular-nums; }
.wl-col-since    { width: 15%; white-space: nowrap; color: var(--wl-ink-soft); font-variant-numeric: tabular-nums; }
.wl-col-desc     { width: 16%; color: var(--wl-ink-soft); }
.wl-col-notifies { width: 33%; }
.wl-col-action   { width: 12%; }

td.wl-col-notifies { overflow: hidden; }

/* Tablica rejestracyjna — wizualnie "blacha", monospaced. */
.wl-plate {
  display: inline-block;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.08em;
  padding: 0.5rem;
  border: 1.5px solid var(--wl-ink);
  border-radius: 5px;
  background: linear-gradient(180deg, #ffffff 0%, #f6f6f3 100%);
  color: var(--wl-ink);
  box-shadow:
    inset 0 -2px 0 rgba(31, 41, 55, 0.08),
    0 1px 0 rgba(255, 255, 255, 0.7);
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
}

/* Licznik trafień. */
.wl-hits {
  display: inline-flex;
  justify-content: center;
  min-width: 2rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background-color: #f4f4f1;
  border: 1px solid var(--wl-line);
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--wl-ink-soft);
}
.wl-hits--zero  { background-color: transparent; border-color: transparent; color: #b5b5b1; font-weight: 500; }
.wl-hits--many  { background-color: #f0faf1; border-color: #b6e1be; color: var(--wl-green-deep); }

.wl-chips {
  display: flex;
  gap: 0.3rem;
  flex-wrap: wrap;
  min-width: 0;
  max-width: 100%;
}
.wl-readonly { color: #b5b5b1; font-weight: 500; }

/* Dwustopniowe usuwanie. */
.wl-confirm {
  display: inline-flex;
  gap: 0.3rem;
  justify-content: flex-end;
  flex-wrap: wrap;
  max-width: 100%;
}
.wl-confirm > .button-compact { flex: 0 0 auto; }

/* =============================================================
   Empty states.
   ============================================================= */
.wl-empty {
  background-color: #fff;
  border: 1px dashed #d1d1cb;
  border-radius: 12px;
  padding: 3rem 1.5rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}
.wl-empty__icon { width: 80px; height: 80px; opacity: 0.85; }
.wl-empty__title { margin: 0; color: var(--wl-ink-soft); font-size: 0.95rem; }

.wl-empty-filter {
  padding: 1.5rem;
  text-align: center;
  color: var(--wl-muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}
.wl-empty-filter p { margin: 0; }

/* =============================================================
   Alerty — kompaktowe pigułki z lewym paskiem koloru.
   ============================================================= */
.wl-alert {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.92rem;
  line-height: 1.45;
  border: 1px solid transparent;
}
.wl-alert__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.85rem;
  flex-shrink: 0;
  color: #fff;
}
.wl-alert__msg { flex: 1; }
.wl-alert--warning {
  background-color: #fff8ec;
  border-color: #f3d9a1;
  border-left: 4px solid #e28b11;
  color: #5a3a07;
}
.wl-alert--warning .wl-alert__icon { background: #e28b11; }
.wl-alert--success {
  background-color: #f0faf1;
  border-color: #b6e1be;
  border-left: 4px solid var(--wl-green);
  color: #1a4f23;
}
.wl-alert--success .wl-alert__icon { background: var(--wl-green); }
.wl-alert--danger {
  background-color: #fef2f2;
  border-color: #f5c2c2;
  border-left: 4px solid #b91c1c;
  color: #6b1d1d;
}
.wl-alert--danger .wl-alert__icon { background: #b91c1c; }

.wl-alert--dismissible {
  position: fixed;
  right: 1.5rem;
  bottom: 1.5rem;
  z-index: 1050;
  min-width: 280px;
  max-width: 440px;
  margin: 0;
  box-shadow: 0 10px 30px -10px rgba(15, 23, 42, 0.25);
  padding-right: 2.75rem;
}
.wl-alert__close {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  background: transparent !important;
  border: 0 !important;
  font-size: 1.3rem;
  cursor: pointer;
  color: inherit !important;
  line-height: 1;
  opacity: 0.55;
  transition: opacity 0.15s ease;
}
.wl-alert__close:hover { opacity: 1; }

/* =============================================================
   Animacje przejść między widokami i wierszami.
   ============================================================= */
.wanted-fade-enter-active,
.wanted-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.wanted-fade-enter-from { opacity: 0; transform: translateY(6px); }
.wanted-fade-leave-to   { opacity: 0; transform: translateY(-6px); }

.wl-row-enter-active,
.wl-row-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease, background-color 0.25s ease;
}
.wl-row-enter-from {
  opacity: 0;
  transform: translateX(-12px);
  background-color: #f0faf1;
}
.wl-row-leave-to {
  opacity: 0;
  transform: translateX(12px);
  background-color: #fff1f1;
}
.wl-row-leave-active { position: relative; }

.wl-toast-enter-active,
.wl-toast-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.wl-toast-enter-from { opacity: 0; transform: translateY(12px); }
.wl-toast-leave-to   { opacity: 0; transform: translateY(12px); }

/* =============================================================
   Override globalnego button-system.css tylko gdzie konieczne.
   ============================================================= */
.button-save {
  border-radius: 6px !important;
  padding: 0.45rem 0.9rem !important;
}

.button-delete {
  border-radius: 4px;
  padding: 0.45rem 0.75rem;
}

@media (max-width: 900px) {
  .wl-stats { grid-template-columns: 1fr 1fr; }
  .wl-stat--groups { grid-column: span 2; }
}
@media (max-width: 768px) {
  .wl-shell { padding: 1rem; }
  .wl-header { flex-direction: column; align-items: stretch; gap: 0.75rem; }
  .wl-header__actions { width: 100%; }
  .wl-stats { grid-template-columns: 1fr; }
  .wl-stat--groups { grid-column: auto; }
  .wl-table { font-size: 0.85rem; }
  .wl-table th, .wl-table td { padding: 0.6rem 0.55rem; }
  .wl-alert--dismissible { left: 1rem; right: 1rem; bottom: 1rem; max-width: none; }
}
</style>
